import React, { useEffect, useMemo, useRef, useState } from 'react';
import { linter, lintGutter } from '@codemirror/lint';
import { yaml } from '@codemirror/lang-yaml';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DraftYamlEditorPane } from './guideline/DraftYamlEditorPane';
import { GuidelineYamlCompareEditor } from './GuidelineYamlCompareEditor';
import { GuidelineDraftRuleForm } from './guideline/GuidelineDraftRuleForm';
import {
  GuidelineAutoExpandSettingsSection,
  GuidelineChunkSettingsSection,
  GuidelineFilterSettingsSection,
  GuidelineOrganizationSection,
  GuidelineSearchSettingsSection,
  GuidelineUnfixableSettingsSection,
} from './guideline/GuidelineMetadataForm';
import type { DraftCompareTarget, DraftCompareTargetId } from '../services/accuracyTuningDraftSeed';
import { emptyDraftMetadata } from '../services/guidelineDraftMetadata';
import { draftContentToYaml, parseDraftContentYaml } from '../services/guidelineDraftYaml';
import type { GuidelineDraftContent, GuidelineDraftRule } from '../types/accuracy';
import '../pages/GuidelineManagementPage.css';
import '../pages/AccuracyTuningPages.css';

type ViewMode = 'form' | 'yaml' | 'compare';

function yamlDiagnostics(view: { state: { doc: { toString: () => string } } }) {
  try {
    parseDraftContentYaml(view.state.doc.toString());
    return [];
  } catch (e) {
    return [
      {
        from: 0,
        to: 1,
        severity: 'error' as const,
        message: e instanceof Error ? e.message : 'YAML エラー',
      },
    ];
  }
}

export function GuidelineDraftEditor({
  draft,
  onChange,
  compareTargets,
  compareTargetId,
  onCompareTargetChange,
  readOnly = false,
  draftTitle = '編集中ドラフト',
}: {
  draft: GuidelineDraftContent;
  onChange: (content: GuidelineDraftContent) => void;
  compareTargets: DraftCompareTarget[];
  compareTargetId: DraftCompareTargetId;
  onCompareTargetChange: (id: DraftCompareTargetId) => void;
  readOnly?: boolean;
  draftTitle?: string;
}) {
  const [viewMode, setViewMode] = useState<ViewMode>('form');
  /** Single raw YAML source shared by YAML tab and compare tab — avoid parse→serialize round trips. */
  const initialYaml = draftContentToYaml(draft);
  const [draftYamlText, setDraftYamlText] = useState(() => initialYaml);
  const draftYamlTextRef = useRef(initialYaml);
  const [yamlEditorKey, setYamlEditorKey] = useState(0);
  const [yamlEditorInitialText, setYamlEditorInitialText] = useState(() => initialYaml);
  const formEditedSinceYamlRef = useRef(false);
  const skipExternalYamlResyncRef = useRef(false);
  const yamlGetTextRef = useRef<(() => string) | null>(null);
  const compareGetTextRef = useRef<(() => string) | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(() => new Set());
  const [collapsedRules, setCollapsedRules] = useState<Set<string>>(() => new Set());

  const metadata = draft.metadata ?? emptyDraftMetadata();

  // External draft updates (chat apply, etc.) — re-serialize only when not editing YAML/compare.
  useEffect(() => {
    if (skipExternalYamlResyncRef.current) {
      skipExternalYamlResyncRef.current = false;
      return;
    }
    if (viewMode === 'yaml' || viewMode === 'compare') return;

    const nextYaml = draftContentToYaml(draft);
    draftYamlTextRef.current = nextYaml;
    setDraftYamlText(nextYaml);
    setYamlEditorInitialText(nextYaml);
    setYamlEditorKey((k) => k + 1);
    formEditedSinceYamlRef.current = false;
  }, [draft, viewMode]);

  const captureLatestYamlText = (): string => {
    const fromCompare = compareGetTextRef.current?.();
    if (fromCompare != null) return fromCompare;
    const fromYaml = yamlGetTextRef.current?.();
    if (fromYaml != null) return fromYaml;
    return draftYamlTextRef.current;
  };

  const syncLatestYamlText = (): string => {
    const text = captureLatestYamlText();
    draftYamlTextRef.current = text;
    setDraftYamlText(text);
    return text;
  };

  const compareTarget = useMemo(
    () => compareTargets.find((t) => t.id === compareTargetId) ?? compareTargets[0],
    [compareTargets, compareTargetId]
  );

  const applyYamlTextToDraft = (): boolean => {
    const text = syncLatestYamlText();
    try {
      skipExternalYamlResyncRef.current = true;
      onChange(parseDraftContentYaml(text));
      return true;
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'YAML の形式が正しくありません');
      return false;
    }
  };

  const switchToForm = () => {
    if (!readOnly && (viewMode === 'yaml' || viewMode === 'compare')) {
      if (!applyYamlTextToDraft()) return;
    }
    setViewMode('form');
  };

  const switchToYaml = () => {
    if (!readOnly && viewMode === 'compare') {
      if (!applyYamlTextToDraft()) return;
    }
    let nextText = draftYamlTextRef.current;
    if (!readOnly && formEditedSinceYamlRef.current) {
      nextText = draftContentToYaml(draft);
      formEditedSinceYamlRef.current = false;
    }
    draftYamlTextRef.current = nextText;
    setDraftYamlText(nextText);
    setYamlEditorInitialText(nextText);
    setYamlEditorKey((k) => k + 1);
    setViewMode('yaml');
  };

  const switchToCompare = () => {
    if (!readOnly && viewMode === 'yaml') {
      if (!applyYamlTextToDraft()) return;
    }
    setViewMode('compare');
  };

  const handleDraftYamlTextChange = (text: string) => {
    draftYamlTextRef.current = text;
    setDraftYamlText(text);
    formEditedSinceYamlRef.current = false;
  };

  const emitDraftChange = (content: GuidelineDraftContent) => {
    if (readOnly) return;
    skipExternalYamlResyncRef.current = true;
    formEditedSinceYamlRef.current = true;
    onChange(content);
  };
  const updateMetadata = (nextMetadata: GuidelineDraftContent['metadata']) => {
    emitDraftChange({ ...draft, metadata: nextMetadata });
  };

  const updateRule = (categoryId: string, ruleId: string, nextRule: GuidelineDraftRule) => {
    emitDraftChange({
      ...draft,
      violation_categories: draft.violation_categories.map((cat) =>
        cat.id !== categoryId
          ? cat
          : {
              ...cat,
              rules: cat.rules.map((rule) => (rule.id !== ruleId ? rule : nextRule)),
            }
      ),
    });
  };

  const toggleCategoryCollapse = (categoryId: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  const toggleRuleCollapse = (ruleKey: string) => {
    setCollapsedRules((prev) => {
      const next = new Set(prev);
      if (next.has(ruleKey)) next.delete(ruleKey);
      else next.add(ruleKey);
      return next;
    });
  };

  return (
    <div className={`guideline-draft-editor${readOnly ? ' guideline-draft-editor-readonly' : ''}`}>
      <div className="guideline-draft-editor-toolbar">
        <div className="guideline-draft-view-tabs">
          <button type="button" className={viewMode === 'form' ? 'active' : ''} onClick={switchToForm}>
            フォーム
          </button>
          <button type="button" className={viewMode === 'yaml' ? 'active' : ''} onClick={switchToYaml}>
            YAML
          </button>
          <button type="button" className={viewMode === 'compare' ? 'active' : ''} onClick={switchToCompare}>
            比較
          </button>
        </div>
        <div className="guideline-draft-editor-meta">
          <span>{draft.guideline_id}</span>
          <span>ベース v{draft.base_version}</span>
        </div>
      </div>

      {viewMode === 'form' && (
        <div className="guideline-content guideline-editor-body">
          <GuidelineOrganizationSection metadata={metadata} onChange={updateMetadata} />
          <GuidelineChunkSettingsSection metadata={metadata} onChange={updateMetadata} />

          <div className="category-section">
            <div className="category-header">
              <h3 className="section-subtitle">違反カテゴリ</h3>
            </div>
            <div className="guideline-items">
              {draft.violation_categories.length === 0 ? (
                <div className="empty-items">
                  <p>違反カテゴリがありません</p>
                </div>
              ) : (
                draft.violation_categories.map((cat, categoryIndex) => {
                  const categoryCollapsed = collapsedCategories.has(cat.id);
                  const rulesCount = cat.rules.length;
                  return (
                    <div key={cat.id} className="guideline-item-card">
                      <div className="item-header">
                        <button
                          type="button"
                          className="item-collapse-btn"
                          onClick={() => toggleCategoryCollapse(cat.id)}
                          title={categoryCollapsed ? '展開' : '折りたたむ'}
                        >
                          {categoryCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                        </button>
                        <div className="item-info">
                          <span className="item-index">#{categoryIndex + 1}</span>
                          <div className="item-title-section">
                            <h4 className="item-title">
                              {cat.violation_category || '(タイトルなし)'}
                            </h4>
                            <span className="item-id">ID: {cat.id}</span>
                          </div>
                          {rulesCount > 0 && (
                            <span className="item-rules-count">{rulesCount}個のルール</span>
                          )}
                        </div>
                      </div>

                      {!categoryCollapsed && (
                        <div className="item-rules-details">
                          {cat.rules.map((rule, ruleIndex) => {
                            const ruleKey = `${cat.id}:${rule.id}`;
                            const ruleCollapsed = collapsedRules.has(ruleKey);
                            return (
                              <div key={rule.id} className="rule-detail-card">
                                <div className="rule-detail-header">
                                  <button
                                    type="button"
                                    className="rule-collapse-btn"
                                    onClick={() => toggleRuleCollapse(ruleKey)}
                                    title={ruleCollapsed ? '展開' : '折りたたむ'}
                                  >
                                    {ruleCollapsed ? (
                                      <ChevronRight size={18} />
                                    ) : (
                                      <ChevronDown size={18} />
                                    )}
                                  </button>
                                  <div className="rule-title-wrapper">
                                    <h5 className="rule-detail-title">
                                      <span className="rule-number">ルール {ruleIndex + 1}</span>
                                      {rule.name ? (
                                        <>
                                          <span className="rule-name-separator">:</span>
                                          <span className="rule-name-text">{rule.name}</span>
                                        </>
                                      ) : null}
                                    </h5>
                                    {rule.type && (
                                      <span className="guideline-draft-rule-type-badge">{rule.type}</span>
                                    )}
                                  </div>
                                </div>

                                {!ruleCollapsed && (
                                  <GuidelineDraftRuleForm
                                    rule={rule}
                                    ruleIndex={ruleIndex}
                                    onChange={(nextRule) => updateRule(cat.id, rule.id, nextRule)}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <GuidelineSearchSettingsSection metadata={metadata} onChange={updateMetadata} />
          <GuidelineFilterSettingsSection metadata={metadata} onChange={updateMetadata} />
          <GuidelineAutoExpandSettingsSection metadata={metadata} onChange={updateMetadata} />
          <GuidelineUnfixableSettingsSection metadata={metadata} onChange={updateMetadata} />
        </div>
      )}

      {viewMode === 'yaml' && (
        <div className="guideline-draft-yaml-wrap">
          <p className="guideline-draft-yaml-note">
            ガイドライン管理画面と同じ構造（guideline_id / base_version / metadata / items）です。
          </p>
          <DraftYamlEditorPane
            editorKey={yamlEditorKey}
            initialText={yamlEditorInitialText}
            extensions={[yaml(), ...(readOnly ? [] : [linter(yamlDiagnostics), lintGutter()])]}
            readOnly={readOnly}
            onChange={handleDraftYamlTextChange}
            registerGetText={(getText) => {
              yamlGetTextRef.current = getText;
            }}
          />
          {!readOnly && (
            <div className="guideline-draft-yaml-actions">
              <button
                type="button"
                className="accuracy-button primary"
                onClick={() => {
                  if (applyYamlTextToDraft()) {
                    window.alert('ドラフトに反映しました');
                  }
                }}
              >
                YAML をドラフトに反映
              </button>
            </div>
          )}
        </div>
      )}

      {viewMode === 'compare' && (
        <div className="guideline-draft-compare-wrap">
          {compareTarget ? (
            <>
              <label className="guideline-draft-compare-select">
                比較対象
                <select
                  value={compareTargetId}
                  onChange={(e) => onCompareTargetChange(e.target.value as DraftCompareTargetId)}
                >
                  {compareTargets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <GuidelineYamlCompareEditor
                original={compareTarget.content}
                yamlText={draftYamlText}
                onYamlTextChange={handleDraftYamlTextChange}
                registerGetDraftYaml={(getText) => {
                  compareGetTextRef.current = getText;
                }}
                onChange={(content) => {
                  if (readOnly) return;
                  skipExternalYamlResyncRef.current = true;
                  onChange(content);
                }}
                originalTitle={compareTarget.label}
                draftTitle={draftTitle}
                readOnly={readOnly}
              />
            </>
          ) : (
            <p className="accuracy-empty">比較対象がありません。</p>
          )}
        </div>
      )}
    </div>
  );
}
